import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { replaceStringWith, validateSortInput } from "../config/helpers";
import useFetch from "./useFetch";
import useAgent from "./useAgent";
import useAuth from "./useAuth";
import useAlert from "./useAlert";

const usePreferencesInit = (type = null, system_preferences = [], show_alert = true) => {
	const dispatch = useDispatch();
	const {topMiddleAlert} = useAlert();
	const preferencesReady = useSelector((state) => state.authReducer.user?.preferences?.preferencesReady);
	const user = useSelector((state) => state.authReducer.user);
	const systemPreferencesReady = useSelector((state) => state.dashboardReducer.system_preferences?.system_preferences_ready);
	const {post, data, response, loading, error} = useFetch(false);
	const {isAgent} = useAuth();
	const {updateDefaultTemplate} = useAgent();
	const { preferences } = user;
	const [values, setValues] = useState([]);

	const updateUserPreferences = (data) => {
		dispatch({
			type: 'AUTH_SET_USER',
			payload: {
				user: data
			}
		})
	}

	useEffect(() => {
		if(type != null && preferencesReady && systemPreferencesReady){
			setValues(fillDefaultPreferences());
		}
	}, [preferencesReady, systemPreferencesReady])
	

	const filterEnabledPreferences = () => system_preferences.filter(preference => preference.status == 'enabled');
	
	// Page Logic

	const fillDefaultPreferences = () => {
        let valuesObj = {}
		if(type != null){
			// return;
			filterEnabledPreferences()?.forEach(preference => {
				const match = user?.preferences?.data?.[type]?.find(user_preference => user_preference.name_alias == preference.name_alias);
				if(typeof match != 'undefined' && match){
					valuesObj[preference.name_alias] = {
						user_preferences_to_user_account_id: match.user_preferences_to_user_account_id,
						user_preference_id: preference.user_preference_id,
						value: match.user_preference,
						status: match.status,
						title: preference.name,
						custom: match.custom_preference !== "0",
						exists: true
					}
				}
				else {
					valuesObj[preference.name_alias] = {
						user_preference_id: preference.user_preference_id,
						value: preference?.default_value || 'default',
						title: preference.name,
						status: preference.status,
						custom: false,
						exists: false
					}
				}
			})
		}
    
        return valuesObj;
    }
	

	useEffect(() => {
		if(type != null && Object.keys(system_preferences).length != 0){
			const data = fillDefaultPreferences();
			setValues(data);
		}
	}, [preferences])
	
	
	const handleUpdate = async (name, value, custom = false, force = false, validation) => {
        let currentPref = values[name];
        if (!currentPref?.user_preference_id && system_preferences?.length > 0) {
            const sysMatch = system_preferences.find((p) => p.name_alias === name);
            if (sysMatch) {
                const userMatch = user?.preferences?.data?.[type]?.find((u) => u.name_alias === name);
                currentPref = {
                    user_preference_id: sysMatch.user_preference_id,
                    user_preferences_to_user_account_id: userMatch?.user_preferences_to_user_account_id,
                    value: sysMatch?.default_value || 'default',
                    title: sysMatch.name,
                    status: sysMatch.status,
                    custom: false,
                    exists: !!userMatch,
                };
            }
        }

        const Obj = {
            ...currentPref,
			...validation,
            value: value,
            custom: custom
        }

        setValues((prevState) => ({
            ...prevState,
            [name] : Obj
        }))

        if(value == '' && custom && !force || value == 'custom' || validation?.error) return;

        const response = await updatePreferences(Obj);
        if(response.status == 'success'){
			const toUpdate = {
				[name] : {
                    ...Obj,
					exists: true,
					user_preferences_to_user_account_id: !Obj?.user_preferences_to_user_account_id ? response.data.new_preference_id : Obj.user_preferences_to_user_account_id
				}
			}

			switch (name) {
				case 'quote_email_template':

					setValues((prevState) => ({
						...prevState,
						...toUpdate
					}))

					const quoteEmailData = {
						template_id: value,
						type: 'quote'
					}

					const quoteEmailReq = await updateDefaultTemplate(quoteEmailData);

					break;
				case 'order_email_template':

					setValues((prevState) => ({
						...prevState,
						...toUpdate
					}))

					const orderEmailData = {
						template_id: value,
						type: 'order'
					}

					const orderEmailReq = await updateDefaultTemplate(orderEmailData);

					break;
			
				default:
					setValues((prevState) => ({
						...prevState,
						...toUpdate
					}))
					break;
			}

        }
    }

    const updateState = (e) => {
        const {value, name} = e.target;
        setValues((prevState) => ({
            ...prevState,
            [name] : {
                ...prevState[name],
                value: value,
                custom: true,
            }
        }))
    }

	const isEnabled = (type) => {
		return values?.[type]?.status == 'enabled' 
	}

	const handleTagClick = (tagLabel, type, sort = false) => {
        const prev = values?.[type]?.value && values[type].value != "" ? replaceStringWith(values[type].value, 'default', '') : "";
		if(tagLabel === 'remove'){
			handlePreferncesChange({target: {name: type, value: ""}}, true, true)
		}
		else {
			if(!sort){
				const spacing = values?.[type]?.value && replaceStringWith(values[type].value, 'default', '') != "" ? ', ' : ''
				const newValue = `${prev}${spacing}${tagLabel}`;
				updateState({ target: { name: type, value: newValue } });
				
			}
			else if(sort){
				const newValue = `${values?.[type]?.value ? values?.[type]?.value : ''}${tagLabel}`;
				updateState({ target: { name: type, value: newValue } });
				handleSortPreferencesChange({ target: { name: type, value: newValue } }, true);
			}
		}
	};

	const handleSortPreferencesChange = (e, custom = false) => {
		const {value, name} = e.target;
		const sort = validateSortInput(value);
		let TEXT = values?.[e.target.name]?.value || "";
		let validation = {error: false, errorText: ""};
		if(sort?.error){
			validation.error = true;
			validation.errorText = "Incorrect synax. This will not save until corrected. (e.g. Column: asc)"
		}
		else {
			TEXT = sort.string;
		}
		const newEvent = {
			target: {
				value: TEXT,
				name: name
			}
		}
		handlePreferncesChange(newEvent, custom, true, validation);
	}

	const handlePreferncesChange = (e, custom = false, force = false, validation = {}) => {
        const {value, name} = e.target;
        if(value == '' && custom && !force) return;
        handleUpdate(name, value, custom, force, validation)

    }

    const handleCheckbox = (e) => {
        const {value, name} = e.target;
        const custom = value == '';
        handleUpdate(name, value, custom)
    }

    const handleSwitch = (e, defaultValue = ['Yes', 'No']) => {
        const {name, checked} = e.target;
		const value = checked ? defaultValue[0] : defaultValue[1];
		const custom = false;
        handleUpdate(name, value, custom)
    }

    const isCustom = (object) => {
        if(values[object].custom) return true;
        else return false;
    }

    const isChecked = (object, name, custom = false) => {
        const obj = values[object];
        if(custom && obj.custom) return true;
        const val = obj.value == name && !isCustom(object);
        return val;
    }

	const isToggled = (object, value = 'Yes') => {
		return values[object].value == value;
	}

	//End of Page Logic

      
	const updatePreferences = async (Obj) => {
		const data = {
			user_preference_id: Obj.user_preference_id,
			user_preferences_to_user_account_id: Obj.user_preferences_to_user_account_id,
			user_preference: Obj.value,
			custom_preference: Obj.custom,
			exists: Obj.exists
		}
		const response = await post('dashboard/customer/preferences/update-preferences', {data: data}, show_alert)

		const prefData = response?.data?.data || response?.data || {};

		let stateData = {
			...user,
			preferences: {
				data: {
					user: prefData.user || []
				},
                preferencesReady: true
			}
		}

		if(isAgent()){
			stateData['preferences']['data']['agent'] = prefData.agent || [];
		}

		updateUserPreferences(stateData);

		return response;
	}

	const resetPreferences = async (type) => {
		const response = await post('dashboard/customer/preferences/reset-preferences', {type: type}, show_alert)

		const prefData = response?.data?.data || response?.data || {};

		let stateData = {
			...user,
			preferences: {
				data: {
					user: prefData.user || []
				},
                preferencesReady: true
			}
		}

		if(isAgent()){
			stateData['preferences']['data']['agent'] = prefData.agent || [];
		}

		updateUserPreferences(stateData);
	}

      
	return {
		updatePreferences,
		pageLogic: {
			updateState,
			fillDefaultPreferences,
			handleUpdate,
			handleTagClick,
			handlePreferncesChange,
			handleSortPreferencesChange,
			resetPreferences,
			handleCheckbox,
			handleSwitch,
			isEnabled,
			isCustom,
			isChecked,
			isToggled,
			values
		},
		request: {
			data,
			response,
			loading,
			error
		},
		preferencesReady,
		systemPreferencesReady
	}
}

export default usePreferencesInit;